/**
 * 評価の削除と復元（評価一覧・評価結果。管理者のみ）。
 *
 * 削除は論理削除（functions/delete-evaluation.mjs）。評価一覧・熟達度の表から外れるだけで、
 * 評価の記録・動画・食味評価は残り、誰がいつ消したかは品目の実行履歴に残る。
 * 誤って消したときは「元に戻す」で戻せる。1日の評価回数は戻らない。
 *
 * 評価文書はクライアントから書けないので Callable を通す（DeleteDish と同じ理由）。
 */
import { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useI18n } from "../lib/i18n";

const functions = getFunctions(app, "asia-northeast1");
const deleteEvaluation = httpsCallable<
  { evaluationId: string; restore?: boolean; reason?: string },
  { ok: boolean; restored: boolean }
>(functions, "deleteEvaluation");

/** 削除の確認欄。「削除」を押すと開く。消えた後は一覧の onSnapshot で行が外れる */
export function DeleteEvaluation({
  evaluationId,
  label,
  onClose,
  onDeleted,
}: {
  evaluationId: string;
  /** 確認文に出す評価の名前（品目名と実行日時） */
  label: string;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const { t } = useI18n();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onDelete() {
    setBusy(true);
    setError(null);
    try {
      await deleteEvaluation({ evaluationId, reason: reason.trim() || undefined });
      onDeleted?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="card warn delete-dish">
      <p>
        <strong>{t("「{name}」の評価を削除します。", { name: label })}</strong>
      </p>
      <p className="small">
        {t("評価一覧と熟達度の表から外れます。評価の記録と動画は消えず、「削除した評価も表示する」から元に戻せます。本日の評価回数は戻りません。誰が削除したかは品目の実行履歴に残ります。")}
      </p>
      <label className="small">
        {t("削除の理由（任意）")}
        <input
          type="text"
          value={reason}
          placeholder={t("例: 撮り直したため")}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {error && <p className="error">{error}</p>}
      <div className="prep-import-row">
        <button className="danger" disabled={busy} onClick={() => void onDelete()}>
          {busy ? t("削除中…") : t("削除する")}
        </button>
        <button className="link" disabled={busy} onClick={onClose}>
          {t("やめる")}
        </button>
      </div>
    </div>
  );
}

/** 削除済みの評価の「元に戻す」（管理者のみ） */
export function RestoreEvaluation({ evaluationId }: { evaluationId: string }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onRestore() {
    setBusy(true);
    setError(null);
    try {
      await deleteEvaluation({ evaluationId, restore: true });
      // 評価文書は onSnapshot で流れてくるので、表示はそのまま戻る
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button className="link" disabled={busy} onClick={() => void onRestore()}>
        {busy ? t("戻しています…") : t("元に戻す")}
      </button>
      {error && <div className="error small">{error}</div>}
    </>
  );
}
